import React, { ComponentType, ReactNode, useContext } from 'react';
import { Modal, ModalProps } from '@cimpress/react-components';

type Omit<T, K extends keyof any> = Pick<T, Exclude<keyof T, K>>;

export type ModalContent = (hide: () => void) => JSX.Element;
export type ModalOptions =
  Omit<ModalProps, 'show' | 'onRequestHide' | 'closeButton' | 'closeOnOutsideClick'>;

export type ModalAction = (title: ReactNode, content: ModalContent, options?: ModalOptions) => void;

export type ModalState = {
  content: ModalContent;
  options: ModalOptions & { title: ReactNode; }
};

// tslint:disable-next-line:variable-name
const ModalContext = React.createContext<ModalAction>(console.log);

// tslint:disable-next-line:variable-name
const injectModal = <Props extends { modal: ModalAction }>(Component: ComponentType<Props>) =>
  (props: Omit<Props, 'modal'>): JSX.Element => (
    <ModalContext.Consumer>
      {modal => {
        const allProps: Props = { ...props, modal } as any;
        return <Component {...allProps} />;
      }}
    </ModalContext.Consumer>
  );

function useModal() {
  const ctx = useContext(ModalContext);
  return ctx;
}

// tslint:disable-next-line:variable-name
const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [modals, setModal] = React.useState<ModalState[]>([]);
  const hideModal = (count: number = 1) => {
    setModal(modals.slice(0, -count));
  };

  window.onkeyup = (event: KeyboardEvent) => {
    if (modals && event.key === 'Escape') {
      hideModal();
    }
  };

  return <>
    <ModalContext.Provider value={
      (title, content, options) => setModal(m => [...m, { content, options: { ...options, title } }])
    }>
      {modals.map(
        (modal, i) => <Modal
          key={i}
          {...modal.options}
          show={true}
          onRequestHide={() => hideModal()}
          closeButton={true}
          closeOnOutsideClick={true}
        >
          {modal.content(hideModal)}
        </Modal>,
      )}
      {children}
    </ModalContext.Provider>
  </>;
};

export {
  ModalContext,
  ModalProvider,
  injectModal,
  useModal,
};
